import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { NAV_LINKS } from '../mock';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const Logo = () => (
  <Link to="/" className="flex items-center gap-2 group">
    <span className="relative flex h-7 w-7 items-center justify-center">
      <span className="absolute inset-0 rounded-md bg-node-style/20 blur-[6px] transition-opacity group-hover:opacity-100" />
      <svg viewBox="0 0 24 24" className="relative h-6 w-6" fill="none">
        <circle cx="5" cy="6" r="2.4" fill="#F5A623" />
        <circle cx="5" cy="18" r="2.4" fill="#A78BFA" />
        <circle cx="19" cy="12" r="2.4" fill="#fff" />
        <path d="M7 6.6 17 11.4M7 17.4 17 12.6" stroke="#3A3A42" strokeWidth="1.3" />
      </svg>
    </span>
    <span className="font-serif-display text-xl font-500 tracking-tight text-white">Idolaris</span>
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'border-b border-border/70 bg-background/80 backdrop-blur-xl' : 'border-b border-transparent'
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-8">
        <Logo />

        <div className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.href}
              className="text-sm text-muted-foreground transition-colors hover:text-white"
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-2 md:flex">
          {user ? (
            <Button
              className="rounded-full bg-white px-5 text-sm font-medium text-black hover:bg-white/90"
              onClick={() => navigate('/app')}
            >
              Open studio
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                className="text-sm text-muted-foreground hover:bg-white/5 hover:text-white"
                onClick={() => navigate('/login')}
              >
                Log in
              </Button>
              <Button
                className="rounded-full bg-white px-5 text-sm font-medium text-black hover:bg-white/90"
                onClick={() => navigate('/register')}
              >
                Sign up for free
              </Button>
            </>
          )}
        </div>

        <button
          className="flex h-9 w-9 items-center justify-center rounded-md text-white md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-border bg-background/95 px-5 py-4 backdrop-blur-xl md:hidden">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.href}
                className="rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:bg-white/5 hover:text-white"
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-2">
              <Button variant="outline" className="w-full border-border" onClick={() => navigate('/login')}>Log in</Button>
              <Button className="w-full rounded-full bg-white text-black hover:bg-white/90" onClick={() => navigate('/register')}>Sign up for free</Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
